import React, { useEffect, useState } from 'react'
import { Button, Form, Input, Spin, Upload, Image, message } from 'antd'
import { FacebookOutlined, InstagramOutlined, TwitterOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux'
import { getSiteSettings, updateSiteSettings } from '../../../redux/actions/siteSettings'
import ProtectedLayout from '../../../layouts/ProtectedLayout';
import type { UploadFile, UploadProps } from 'antd/es/upload/interface';

const SiteSettings = () => {
    const dispatch = useDispatch()
    const { siteSettings, loading } = useSelector((state: any) => state.siteSettings)
    const [form] = Form.useForm();
    const [previewLogo, setPreviewLogo] = useState('');
    const [previewFavicon, setPreviewFavicon] = useState('');
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [deletedSlides, setDeletedSlides] = useState<string[]>([]);

    useEffect(() => {
        dispatch<any>(getSiteSettings());
    }, [dispatch]);

    // siteSettings değiştiğinde formu doldur
    useEffect(() => {
        if (siteSettings) {
            form.setFieldsValue({
                siteName: siteSettings.siteName || "",
                logo: siteSettings.logo || "",
                favicon: siteSettings.favicon || "",
                email: siteSettings.email || "",
                acilisSaati: siteSettings.acilisSaati || "",
                kapanisSaati: siteSettings.kapanisSaati || "",
                instagram: siteSettings.instagram || "",
                twitter: siteSettings.twitter || "",
                facebook: siteSettings.facebook || "",
                metaDescription: siteSettings.metaDescription || "",
                metaKeywords: siteSettings.metaKeywords || "",
                metaTitle: siteSettings.metaTitle || "",
                coordinates: siteSettings.coordinates || ""
            });

            if (siteSettings.logo) {
                setPreviewLogo(siteSettings.logo);
            }
            if (siteSettings.slides && Array.isArray(siteSettings.slides)) {
                // Mevcut slides'ları fileList'e dönüştür
                const slides = siteSettings.slides.map((url: string, index: number) => ({
                    uid: `-${index}`,
                    name: `slide-${index}`,
                    status: 'done',
                    url: url,
                }));
                setFileList(slides);
            }
        }
    }, [siteSettings, form]);

    const handleLogoChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 2 * 1024 * 1024) {
                message.error('Dosya boyutu 2MB\'dan büyük olamaz');
                return;
            }

            if (!file.type.match(/image\/(jpeg|png|gif|webp)/)) {
                message.error('Sadece JPG, PNG, GIF veya WEBP formatındaki dosyalar yüklenebilir');
                return;
            }

            try {
                const base64 = await convertToBase64(file);
                setPreviewLogo(base64 as string);
                form.setFieldValue('logo', base64);
            } catch (error) {
                console.error('Logo dönüştürme hatası:', error);
                message.error('Logo yüklenirken bir hata oluştu');
            }
        }
    };

    const handleFaviconChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            const base64 = await convertToBase64(file);
            setPreviewFavicon(base64 as string);
            form.setFieldValue('favicon', base64);
        }
    };

    const convertToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = error => reject(error);
        });
    };

    const handleFinish = async (values: any) => {
        const formData = new FormData();
        
        // Diğer alanları ekle
        Object.keys(values).forEach(key => {
            if (key !== 'slides') {
                formData.append(key, values[key]);
            }
        });

        // Silinen slides'ları ekle
        if (deletedSlides.length > 0) {
            formData.append('deletedSlides', JSON.stringify(deletedSlides));
        }

        // Mevcut ve yeni dosyaları ekle
        fileList.forEach(file => {
            if (file.originFileObj) {
                // Yeni yüklenen dosyalar
                formData.append('slides', file.originFileObj);
            } else if (file.url) {
                // Mevcut dosyalar
                formData.append('existingSlides', file.url);
            }
        });

        dispatch<any>(updateSiteSettings(formData));
    };

    const uploadProps: UploadProps = {
        onRemove: (file) => {
            const index = fileList.indexOf(file);
            const newFileList = fileList.slice();
            newFileList.splice(index, 1);
            setFileList(newFileList);

            // Eğer silinen dosya mevcut bir slide ise (url'i varsa), deletedSlides listesine ekle
            if (file.url) {
                setDeletedSlides(prev => [...prev, file.url as string]);
            }
        },
        beforeUpload: (file) => {
            if (file.size > 2 * 1024 * 1024) {
                message.error('Dosya boyutu 2MB\'dan büyük olamaz');
                return Upload.LIST_IGNORE;
            }
            if (!file.type.match(/image\/(jpeg|png|gif|webp)/)) {
                message.error('Sadece JPG, PNG, GIF veya WEBP formatındaki dosyalar yüklenebilir');
                return Upload.LIST_IGNORE;
            }
            return false;
        },
        fileList,
        onChange: ({ fileList: newFileList }) => {
            setFileList(newFileList);
        },
        listType: "picture-card",
        maxCount: 10,
    };

    if (!siteSettings) {
        return <div className="flex w-full justify-center items-center h-screen"><Spin size="large" /></div>;
    }

    return (
        <ProtectedLayout>
            <div className="container mx-auto px-4 py-8">
            <h1 className="text-2xl font-bold mb-6">Site Ayarları</h1>
            {loading ? <Spin size="large" /> : <Form
                form={form}
                layout="vertical"
                onFinish={handleFinish}
                className="space-y-6"
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
            >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Form.Item
                        label="Site Logo"
                        name="logo"
                        valuePropName="file"
                    >
                        <div className="mt-1 flex items-center space-x-4">
                            {previewLogo && (
                                <img
                                    src={previewLogo}
                                    alt="Logo önizleme"
                                    className="h-20 w-20 object-contain border rounded"
                                />
                            )}
                            <input
                                type="file"
                                accept="image/jpeg,image/png,image/gif,image/webp"
                                onChange={handleLogoChange}
                                className="block w-full text-sm text-gray-500
                                    file:mr-4 file:py-2 file:px-4
                                    file:rounded-full file:border-0
                                    file:text-sm file:font-semibold
                                    file:bg-indigo-50 file:text-indigo-700
                                    hover:file:bg-indigo-100"
                            />
                        </div>
                    </Form.Item>
                    <Form.Item
                        label="Site Favicon"
                        name="favicon"
                        valuePropName="file"
                    >
                        <div className="mt-1 flex items-center space-x-4">
                            {(previewFavicon || form.getFieldValue("favicon")) && (
                                <img
                                    src={previewFavicon ? previewFavicon : (typeof form.getFieldValue("favicon") === "string" ? form.getFieldValue("favicon") : "")}
                                    alt="Favicon önizleme"
                                    className="h-20 w-20 object-contain border rounded"
                                />
                            )}
                            <input
                                type="file"
                                accept="image/jpeg,image/png,image/gif,image/webp,image/x-icon,.ico"
                                onChange={handleFaviconChange}
                                className="block w-full text-sm text-gray-500
                                    file:mr-4 file:py-2 file:px-4
                                    file:rounded-full file:border-0
                                    file:text-sm file:font-semibold
                                    file:bg-indigo-50 file:text-indigo-700
                                    hover:file:bg-indigo-100"
                            />
                        </div>
                    </Form.Item>
                    <Form.Item
                        label="Site Slaytları"
                        name="slides"
                    >
                        <Upload {...uploadProps}>
                            {fileList.length >= 10 ? null : (
                                <div>
                                    <PlusOutlined />
                                    <div style={{ marginTop: 8 }}>Yükle</div>
                                </div>
                            )}
                        </Upload>
                    </Form.Item>
                    <Form.Item
                        label="Site Adı"
                        name="siteName"
                        rules={[{ required: true, message: 'Site adı zorunludur' }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        label="Site E-posta"
                        name="email"
                        rules={[{ type: 'email', message: 'Geçerli bir e-posta giriniz' }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        label="Site Açılış Saati"
                        name="acilisSaati"
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        label="Site Kapanış Saati"
                        name="kapanisSaati"
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        label={
                            <span>
                                <InstagramOutlined className="mr-1" />
                                Instagram
                            </span>
                        }
                        name="instagram"
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        label={
                            <span>
                                <FacebookOutlined className="mr-1" />
                                Facebook
                            </span>
                        }
                        name="facebook"
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        label={
                            <span>
                                <TwitterOutlined className="mr-1" />
                                Twitter
                            </span>
                        }
                        name="twitter"
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        label="Site Meta Açıklaması"
                        name="metaDescription"
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        label="Site Meta Anahtar Kelimeleri"
                        name="metaKeywords"
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        label="Site Meta Başlığı"
                        name="metaTitle"
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        label="Site Koordinatları"
                        name="coordinates"
                    >
                        <Input />
                    </Form.Item>
                </div>
                <Form.Item className="mt-6">
                    <Button
                        type="primary"
                        htmlType="submit"
                        className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Ayarları Kaydet
                    </Button>
                </Form.Item>
            </Form>}
        </div>
        </ProtectedLayout>
    )
}

export default SiteSettings